import React, { useRef, useState, useEffect } from "react";
import { useReactToPrint } from "react-to-print";
import {
    Dialog,
    DialogActions,
    DialogContent,
    Button,
    Grid,
    TextField,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Select,
    FormControl,
    InputLabel,
    Divider,
} from "@material-ui/core";
import { config as appConfig } from "../../config";
import { getFirstAndLastNames } from "../../utils/Functions";
import { tagPresets } from "../../config/tagPresets";
import { getTagConfig, saveTagConfig, exportTagConfig, importTagConfig } from "../../utils/tagConfig";
import { toast } from "react-toastify";

const dummyPerson = {
    id: "0001",
    name: "Fulano de Tal",
    company: "Empresa Exemplo S.A.",
    role: "Diretor Executivo",
};

const TagEditor = ({ eventId = 1, person, dialogPrintOpen, handleClosePrint }) => {
    const componentRef = useRef();
    const activePerson = person && person.id ? person : dummyPerson;

    // Estados do layout da etiqueta
    const [presetName, setPresetName] = useState("");
    const [widthMm, setWidthMm] = useState(90);
    const [heightMm, setHeightMm] = useState(50);
    
    const [showQr, setShowQr] = useState(true);
    const [qrCodePosition, setQrCodePosition] = useState({ top: 12, left: 8 });
    const [qrCodeSizeMm, setQrCodeSizeMm] = useState(25);
    
    const [showName, setShowName] = useState(true);
    const [namePosition, setNamePosition] = useState({ top: 13, left: 40 });
    const [nameFontSize, setNameFontSize] = useState(16);
    
    const [showCompany, setShowCompany] = useState(true);
    const [companyPosition, setCompanyPosition] = useState({ top: 23, left: 40 });
    const [companyFontSize, setCompanyFontSize] = useState(12);
    
    const [showRole, setShowRole] = useState(true);
    const [rolePosition, setRolePosition] = useState({ top: 30, left: 40 });
    const [roleFontSize, setRoleFontSize] = useState(10);

    // Estado do painel de importação/exportação JSON
    const [showJsonPanel, setShowJsonPanel] = useState(false);
    const [jsonInput, setJsonInput] = useState("");

    // Carregar a configuração ativa ao abrir o modal
    useEffect(() => {
        if (dialogPrintOpen && eventId) {
            const activeConfig = getTagConfig(eventId);
            applyConfigToStates(activeConfig);
            setPresetName(""); // Resetar a seleção de preset
            setShowJsonPanel(false);
            setJsonInput("");
        }
    }, [dialogPrintOpen, eventId]);

    // Aplica um objeto de configuração aos estados do React
    const applyConfigToStates = (cfg) => {
        if (!cfg) return;
        setWidthMm(cfg.widthMm ?? 90);
        setHeightMm(cfg.heightMm ?? 50);
        
        setShowQr(cfg.showQr ?? true);
        setQrCodePosition(cfg.qrCodePosition ?? { top: 12, left: 8 });
        setQrCodeSizeMm(cfg.qrCodeSizeMm ?? 25);
        
        setShowName(cfg.showName ?? true);
        setNamePosition(cfg.namePosition ?? { top: 13, left: 40 });
        setNameFontSize(cfg.nameFontSize ?? 16);
        
        setShowCompany(cfg.showCompany ?? true);
        setCompanyPosition(cfg.companyPosition ?? { top: 23, left: 40 });
        setCompanyFontSize(cfg.companyFontSize ?? 12);
        
        setShowRole(cfg.showRole ?? true);
        setRolePosition(cfg.rolePosition ?? { top: 30, left: 40 });
        setRoleFontSize(cfg.roleFontSize ?? 10);
    };

    // Retorna a configuração atual agregada
    const getCurrentConfig = () => {
        return {
            widthMm: Number(widthMm),
            heightMm: Number(heightMm),
            showQr,
            qrCodePosition: { top: Number(qrCodePosition.top), left: Number(qrCodePosition.left) },
            qrCodeSizeMm: Number(qrCodeSizeMm),
            showName,
            namePosition: { top: Number(namePosition.top), left: Number(namePosition.left) },
            nameFontSize: Number(nameFontSize),
            showCompany,
            companyPosition: { top: Number(companyPosition.top), left: Number(companyPosition.left) },
            companyFontSize: Number(companyFontSize),
            showRole,
            rolePosition: { top: Number(rolePosition.top), left: Number(rolePosition.left) },
            roleFontSize: Number(roleFontSize),
        };
    };

    // Manipulador de mudança de preset
    const handleSelectPreset = (e) => {
        const val = e.target.value;
        setPresetName(val);
        
        if (tagPresets.printers[val]) {
            applyConfigToStates(tagPresets.printers[val]);
            toast.info(`Gabarito "${tagPresets.printers[val].name}" aplicado!`);
        }
    };

    // Ação de Salvar Configuração
    const handleSave = () => {
        const currentCfg = getCurrentConfig();
        const success = saveTagConfig(eventId, currentCfg);
        if (success) {
            toast.success("Configuração do layout da etiqueta salva com sucesso!");
            handleClosePrint();
        } else {
            toast.error("Erro ao salvar configuração.");
        }
    };

    // Ação de Imprimir
    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
        onAfterPrint: () => {
            toast.success("Envio para impressora realizado!");
        },
        pageStyle: `
            @page {
                size: ${widthMm}mm ${heightMm}mm;
                margin: 0;
            }
            body {
                margin: 0;
                background: #fff;
                -webkit-print-color-adjust: exact;
            }
            @media print {
                .printable-tag {
                    border: none !important;
                    box-shadow: none !important;
                    background: white !important;
                    margin: 0 !important;
                }
            }
        `,
    });

    // Funções de Importação e Exportação JSON
    const handleExportJson = () => {
        const currentCfg = getCurrentConfig();
        const jsonStr = exportTagConfig(currentCfg);
        setJsonInput(jsonStr);
        setShowJsonPanel(true);
        
        // Copiar para o clipboard automaticamente
        try {
            navigator.clipboard.writeText(jsonStr);
            toast.success("Código de configuração copiado para a área de transferência!");
        } catch (err) {
            toast.info("Configuração gerada abaixo. Copie o texto manualmente.");
        }
    };

    const handleImportJson = () => {
        if (!jsonInput.trim()) {
            toast.warn("Por favor, cole um código JSON válido antes de importar.");
            return;
        }
        
        const imported = importTagConfig(jsonInput);
        if (imported) {
            applyConfigToStates(imported);
            setPresetName("");
            setShowJsonPanel(false);
            toast.success("Layout importado com sucesso! Clique em Salvar para fixar.");
        } else {
            toast.error("JSON inválido. Certifique-se de colar o formato correto exportado pelo sistema.");
        }
    };

    return (
        <Dialog onClose={handleClosePrint} open={dialogPrintOpen} maxWidth="md" fullWidth>
            <DialogContent style={{ padding: "24px" }}>
                <h3 style={{ marginTop: 0, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>Configuração da Tag de Impressão - Evento #{eventId}</span>
                    <span style={{ fontSize: "14px", fontWeight: "normal", color: "#666" }}>
                        Visualização técnica
                    </span>
                </h3>

                <Grid container spacing={3}>
                    {/* Painel Esquerdo: Formulário de Configuração */}
                    <Grid item xs={12} md={6}>
                        {/* Seletor de Presets */}
                        <FormControl variant="outlined" fullWidth size="small" style={{ marginBottom: "20px" }}>
                            <InputLabel id="preset-select-label">Gabarito de Impressora</InputLabel>
                            <Select
                                labelId="preset-select-label"
                                value={presetName}
                                onChange={handleSelectPreset}
                                label="Gabarito de Impressora"
                            >
                                <MenuItem value="">
                                    <em>-- Nenhum preset selecionado --</em>
                                </MenuItem>
                                {Object.entries(tagPresets.printers).map(([key, value]) => (
                                    <MenuItem key={key} value={key}>
                                        {value.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Dimensões Gerais */}
                        <h4 style={{ margin: "10px 0" }}>Dimensões Físicas (mm)</h4>
                        <Grid container spacing={2} style={{ marginBottom: "15px" }}>
                            <Grid item xs={6}>
                                <TextField
                                    label="Largura (mm)"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={widthMm}
                                    onChange={(e) => setWidthMm(Number(e.target.value))}
                                    type="number"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Altura (mm)"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={heightMm}
                                    onChange={(e) => setHeightMm(Number(e.target.value))}
                                    type="number"
                                />
                            </Grid>
                        </Grid>

                        <Divider style={{ margin: "15px 0" }} />

                        {/* Configuração do QR Code */}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <h4 style={{ margin: "5px 0" }}>QR Code</h4>
                            <FormControlLabel
                                control={<Checkbox checked={showQr} onChange={(e) => setShowQr(e.target.checked)} color="primary" />}
                                label="Exibir"
                            />
                        </div>
                        {showQr && (
                            <Grid container spacing={2} style={{ marginBottom: "15px" }}>
                                <Grid item xs={4}>
                                    <TextField
                                        label="Top (mm)"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={qrCodePosition.top}
                                        onChange={(e) => setQrCodePosition({ ...qrCodePosition, top: Number(e.target.value) })}
                                        type="number"
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        label="Left (mm)"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={qrCodePosition.left}
                                        onChange={(e) => setQrCodePosition({ ...qrCodePosition, left: Number(e.target.value) })}
                                        type="number"
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        label="Tamanho (mm)"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={qrCodeSizeMm}
                                        onChange={(e) => setQrCodeSizeMm(Number(e.target.value))}
                                        type="number"
                                    />
                                </Grid>
                            </Grid>
                        )}

                        <Divider style={{ margin: "15px 0" }} />

                        {/* Configuração do Nome */}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <h4 style={{ margin: "5px 0" }}>Nome do Participante</h4>
                            <FormControlLabel
                                control={<Checkbox checked={showName} onChange={(e) => setShowName(e.target.checked)} color="primary" />}
                                label="Exibir"
                            />
                        </div>
                        {showName && (
                            <Grid container spacing={2} style={{ marginBottom: "15px" }}>
                                <Grid item xs={4}>
                                    <TextField
                                        label="Top (mm)"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={namePosition.top}
                                        onChange={(e) => setNamePosition({ ...namePosition, top: Number(e.target.value) })}
                                        type="number"
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        label="Left (mm)"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={namePosition.left}
                                        onChange={(e) => setNamePosition({ ...namePosition, left: Number(e.target.value) })}
                                        type="number"
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        label="Fonte (pt)"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={nameFontSize}
                                        onChange={(e) => setNameFontSize(Number(e.target.value))}
                                        type="number"
                                    />
                                </Grid>
                            </Grid>
                        )}

                        <Divider style={{ margin: "15px 0" }} />

                        {/* Configuração da Empresa */}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <h4 style={{ margin: "5px 0" }}>Empresa (Company)</h4>
                            <FormControlLabel
                                control={<Checkbox checked={showCompany} onChange={(e) => setShowCompany(e.target.checked)} color="primary" />}
                                label="Exibir"
                            />
                        </div>
                        {showCompany && (
                            <Grid container spacing={2} style={{ marginBottom: "15px" }}>
                                <Grid item xs={4}>
                                    <TextField
                                        label="Top (mm)"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={companyPosition.top}
                                        onChange={(e) => setCompanyPosition({ ...companyPosition, top: Number(e.target.value) })}
                                        type="number"
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        label="Left (mm)"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={companyPosition.left}
                                        onChange={(e) => setCompanyPosition({ ...companyPosition, left: Number(e.target.value) })}
                                        type="number"
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        label="Fonte (pt)"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={companyFontSize}
                                        onChange={(e) => setCompanyFontSize(Number(e.target.value))}
                                        type="number"
                                    />
                                </Grid>
                            </Grid>
                        )}

                        <Divider style={{ margin: "15px 0" }} />

                        {/* Configuração do Cargo */}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <h4 style={{ margin: "5px 0" }}>Cargo (Role)</h4>
                            <FormControlLabel
                                control={<Checkbox checked={showRole} onChange={(e) => setShowRole(e.target.checked)} color="primary" />}
                                label="Exibir"
                            />
                        </div>
                        {showRole && (
                            <Grid container spacing={2} style={{ marginBottom: "15px" }}>
                                <Grid item xs={4}>
                                    <TextField
                                        label="Top (mm)"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={rolePosition.top}
                                        onChange={(e) => setRolePosition({ ...rolePosition, top: Number(e.target.value) })}
                                        type="number"
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        label="Left (mm)"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={rolePosition.left}
                                        onChange={(e) => setRolePosition({ ...rolePosition, left: Number(e.target.value) })}
                                        type="number"
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        label="Fonte (pt)"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={roleFontSize}
                                        onChange={(e) => setRoleFontSize(Number(e.target.value))}
                                        type="number"
                                    />
                                </Grid>
                            </Grid>
                        )}
                    </Grid>

                    {/* Painel Direito: Preview e Importação/Exportação */}
                    <Grid item xs={12} md={6} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <h4 style={{ alignSelf: "flex-start", marginTop: 0 }}>Visualização da Credencial (Escala Fisiológica)</h4>
                        
                        {/* Área de Preview Física */}
                        <div 
                            style={{ 
                                width: "100%", 
                                display: "flex", 
                                justifyContent: "center", 
                                alignItems: "center",
                                background: "#eceff1",
                                padding: "20px",
                                borderRadius: "8px",
                                minHeight: "260px",
                                boxSizing: "border-box",
                                marginBottom: "20px"
                            }}
                        >
                            <div
                                ref={componentRef}
                                className="printable-tag"
                                style={{
                                    width: `${widthMm}mm`,
                                    height: `${heightMm}mm`,
                                    position: "relative",
                                    boxSizing: "border-box",
                                    overflow: "hidden",
                                    backgroundColor: "#ffffff",
                                    border: "1px dashed #78909c",
                                    boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
                                    transition: "width 0.2s, height 0.2s",
                                }}
                            >
                                {/* Elemento: QR Code */}
                                {showQr && (
                                    <div
                                        style={{
                                            position: "absolute",
                                            top: `${qrCodePosition.top}mm`,
                                            left: `${qrCodePosition.left}mm`,
                                            width: `${qrCodeSizeMm}mm`,
                                            height: `${qrCodeSizeMm}mm`,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <img
                                            src={`${appConfig.QRCODE_HOST || "https://api.qrserver.com/v1/create-qr-code"}/?size=150x150&data=${activePerson.id}`}
                                            alt="QR Code"
                                            style={{ width: "100%", height: "100%" }}
                                        />
                                    </div>
                                )}

                                {/* Elemento: Nome */}
                                {showName && (
                                    <div
                                        style={{
                                            position: "absolute",
                                            top: `${namePosition.top}mm`,
                                            left: `${namePosition.left}mm`,
                                            fontSize: `${nameFontSize}pt`,
                                            fontWeight: "700",
                                            fontFamily: "'Inter', 'Roboto', sans-serif",
                                            color: "#000000",
                                            whiteSpace: "nowrap",
                                            lineHeight: "1.2",
                                        }}
                                    >
                                        {activePerson.name ? getFirstAndLastNames(activePerson.name) : ""}
                                    </div>
                                )}

                                {/* Elemento: Empresa */}
                                {showCompany && activePerson.company && (
                                    <div
                                        style={{
                                            position: "absolute",
                                            top: `${companyPosition.top}mm`,
                                            left: `${companyPosition.left}mm`,
                                            fontSize: `${companyFontSize}pt`,
                                            fontWeight: "600",
                                            fontFamily: "'Inter', 'Roboto', sans-serif",
                                            color: "#37474f",
                                            whiteSpace: "nowrap",
                                            lineHeight: "1.2",
                                        }}
                                    >
                                        {activePerson.company}
                                    </div>
                                )}

                                {/* Elemento: Cargo */}
                                {showRole && activePerson.role && (
                                    <div
                                        style={{
                                            position: "absolute",
                                            top: `${rolePosition.top}mm`,
                                            left: `${rolePosition.left}mm`,
                                            fontSize: `${roleFontSize}pt`,
                                            fontStyle: "italic",
                                            fontFamily: "'Inter', 'Roboto', sans-serif",
                                            color: "#546e7a",
                                            whiteSpace: "nowrap",
                                            lineHeight: "1.2",
                                        }}
                                    >
                                        {activePerson.role}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Ações Técnicas: Import/Export */}
                        <div style={{ width: "100%", display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                            <Button 
                                variant="outlined" 
                                size="small" 
                                color="default"
                                onClick={handleExportJson}
                            >
                                Copiar/Exportar Configuração
                            </Button>
                            <Button 
                                variant="outlined" 
                                size="small" 
                                color="default"
                                onClick={() => {
                                    setJsonInput("");
                                    setShowJsonPanel(!showJsonPanel);
                                }}
                            >
                                Importar
                            </Button>
                        </div>

                        {/* Painel JSON */}
                        {showJsonPanel && (
                            <div style={{ width: "100%", marginTop: "15px" }}>
                                <TextField
                                    label="Configuração JSON"
                                    multiline
                                    rows={6}
                                    variant="outlined"
                                    fullWidth
                                    value={jsonInput}
                                    onChange={(e) => setJsonInput(e.target.value)}
                                    placeholder='Cole o JSON de configuração exportado aqui...'
                                    style={{ fontFamily: "monospace", fontSize: "12px", marginBottom: "10px" }}
                                />
                                <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                                    <Button 
                                        variant="contained" 
                                        color="secondary" 
                                        size="small"
                                        onClick={() => setShowJsonPanel(false)}
                                    >
                                        Fechar
                                    </Button>
                                    <Button 
                                        variant="contained" 
                                        color="primary" 
                                        size="small"
                                        onClick={handleImportJson}
                                    >
                                        Aplicar JSON
                                    </Button>
                                </div>
                            </div>
                        )}
                    </Grid>
                </Grid>
            </DialogContent>
            
            <DialogActions style={{ padding: "16px 24px", background: "#f5f5f5" }}>
                <Button 
                    variant="contained" 
                    onClick={handleClosePrint} 
                    style={{ background: "#9e9e9e", color: "white" }}
                >
                    Fechar
                </Button>
                
                {/* Botão de Teste de Impressão imediato */}
                <Button 
                    variant="contained" 
                    onClick={handlePrint} 
                    style={{ background: "#ff9800", color: "white" }}
                >
                    Testar Impressão
                </Button>
                
                <Button 
                    variant="contained" 
                    onClick={handleSave} 
                    color="primary"
                >
                    Salvar Como Padrão
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TagEditor;
